<template>
	<JBox><canvas tabindex="0" :height="height" :width="width" ref="editor"/></JBox>
</template>

<script>
import { fabric } from "fabric"
import ColorHash from "color-hash"

import api from "@/api/api"
import { mutation } from "../../../api/queries.js"

const drawingToFabric = (drawing, scale, activeTool) => {
	const width = (drawing.point2.x - drawing.point1.x) * scale
	const height = (drawing.point2.y - drawing.point1.y) * scale
	const color = new ColorHash()
	return {
		type: "box",
		id: drawing.id,
		left: drawing.point1.x * scale,
		top: drawing.point1.y * scale,

		width,
		height,

		point1: drawing.point1,
		point2: drawing.point2,

		fill: `rgba(${color.rgb(drawing.id)}, 0.5)`,
		liquidSource: drawing.liquidSource,
		hasRotatingPoint: false,
		lockMovementX: activeTool === "select" ? false : true,
		lockMovementY: activeTool === "select" ? false : true,
		selectable: activeTool === "select" ? true : false,
		hasBorders: false,
		borderColor: `rgba(${color.rgb(drawing.id)}, 0.5)`,
		textAlignment: drawing.textAlignment,
		verticalAlignment: drawing.verticalAlignment
	}
}

const generateDrawingObject = (options, scale) => {
	const { target } = options
	const { id, left, top } = target

	const height = target.height * scale
	const width = target.width * scale

	let pointX1 = left / scale
	let pointY1 = top / scale

	let pointX2 = (left + (width * target.scaleX) / scale) / scale
	let pointY2 = (top + (height * target.scaleY) / scale) / scale

	return {
		id,
		hasRotatingPoint: false,
		width,
		height,
		point1: {
			x: pointX1,
			y: pointY1
		},
		point2: {
			x: pointX2,
			y: pointY2
		}
	}
}

export default {
	props: {
		templateId: {
			type: String
		},
		drawings: {
			type: Array
		},
		height: {
			type: Number
		},
		width: {
			type: Number
		},
		page: {
			type: Number,
			default: 1
		},
		scale: {
			type: Number,
			default: 1
		},
		activeTool: {
			type: String // select or rectangle
		},
		isSaving: {
			type: Boolean
		}
	},

	data() {
		return {
			editor: null,
			selectedDrawingId: null,
			mouseDown: null,
			mouseUp: null,

			rect: null,
			isDown: null,
			origX: null,
			origY: null
		}
	},

	mounted() {
		this.editor = new fabric.Canvas(this.$refs.editor, { selection: false })

		// triggers when drawing box is moved or resized
		this.editor.on("object:modified", async options => {
			let drawingObj = await generateDrawingObject(options, this.scale)

			// prevent from selecting new drawing if editor is saving
			if (this.activeTool === "select" && !this.isSaving) {
				this.selectedDrawingId = options?.target?.id
				// update state of drawing in db
				this.updatePdfDrawing({ id: options?.target?.id, drawingObj })
				// update local state of drawing
				this.$emit("drawing:updated", {
					point1: drawingObj?.point1,
					point2: drawingObj?.point2
				})
			}
		})

		this.editor.on("mouse:down", options => {
			this.mouseDown = options.pointer
			// prevent from selecting new drawing if editor is saving
			if (this.activeTool === "select" && options.target && !this.isSaving) {
				this.selectedDrawingId = options?.target?.id
				this.$emit("drawing:selected", this.selectedDrawingId)
			}
			if (this.activeTool === "rectangle") {
				this.isDown = true
				let pointer = this.editor.getPointer(options.e)
				this.origX = pointer.x
				this.origY = pointer.y
				pointer = this.editor.getPointer(options.e)
				this.rect = new fabric.Rect({
					left: this.origX,
					top: this.origY,
					originX: "left",
					originY: "top",
					width: pointer.x - this.origX,
					height: pointer.y - this.origY,
					angle: 0,
					transparentCorners: false
				})
				this.editor.add(this.rect)
			}
		})

		this.editor.on("mouse:move", o => {
			if (this.activeTool === "rectangle") {
				if (!this.isDown) return
				let pointer = this.editor.getPointer(o.e)

				if (this.origX > pointer.x) {
					this.rect.set({ left: Math.abs(pointer.x) })
				}
				if (this.origY > pointer.y) {
					this.rect.set({ top: Math.abs(pointer.y) })
				}

				this.rect.set({ width: Math.abs(this.origX - pointer.x) })
				this.rect.set({ height: Math.abs(this.origY - pointer.y) })

				this.editor.renderAll()
			}
		})

		this.editor.on("mouse:up", options => {
			this.isDown = false
			this.mouseUp = options.pointer

			// stops creating draawings on clicks
			if (this.mouseUp.x === this.mouseDown.x && this.mouseUp.y === this.mouseDown.y) return

			if (this.activeTool === "rectangle") {
				const createInput = {
					page: this.page,
					point1: {
						x: this.mouseDown.x / this.scale,
						y: this.mouseDown.y / this.scale
					},
					point2: {
						x: this.mouseUp.x / this.scale,
						y: this.mouseUp.y / this.scale
					}
				}
				this.addPdfDrawing(createInput)
			}
		})

		window.addEventListener("keydown", this.handleKeyDown)
	},

	beforeDestroy() {
		window.removeEventListener("keydown", this.handleKeyDown)
	},

	methods: {
		async renderDrawings() {
			this.editor.clear()

			this.editor.setHeight(this.height)
			this.editor.setWidth(this.width)

			const drawings = this.drawings.filter(drawing => drawing.page === this.page)

			await drawings.map(drawing => {
				const renderedDrawing = new fabric.Rect(drawingToFabric(drawing, this.scale, this.activeTool))
				this.editor.add(renderedDrawing)
				return renderedDrawing
			})

			this.editor.renderAll()
		},
		async updatePdfDrawing({ id, drawingObj }) {
			const variables = {
				templateId: this.templateId,
				drawingId: id,
				input: {
					point1: drawingObj?.point1,
					point2: drawingObj?.point2
				}
			}
			const res = await api({ query: mutation.updatePdfDrawing, variables })
			if (res?.data?.updatePdfDrawing?.status !== "success") {
				this.$emit("drawing:errorMessage", res?.errors?.[0] || res?.data?.updatePdfDrawing?.errors?.[0])
				this.$emit("drawing:error")
			}
		},
		async addPdfDrawing(payload) {
			const variables = {
				templateId: this.templateId,
				input: payload
			}
			const res = await api({ query: mutation.addPdfDrawing, variables })
			if (res?.data?.addPdfDrawing?.status !== "success") {
				this.$emit("drawing:errorMessage", res?.errors?.[0] || res?.data?.addPdfDrawing?.errors?.[0])
				this.$emit("drawing:error")
			}

			this.$emit("drawing:added", { id: res?.data?.addPdfDrawing?.value, ...payload })
		},
		async removePdfDrawing() {
			const res = await api({
				query: mutation.removePdfDrawing,
				variables: {
					templateId: this.templateId,
					drawingId: this.selectedDrawingId
				}
			})
			if (res?.data?.removePdfDrawing?.status !== "success") {
				this.$emit("drawing:errorMessage", res?.errors?.[0] || res?.data?.removePdfDrawing?.errors?.[0])
				this.$emit("drawing:error")
			}
		},
		handleKeyDown(event) {
			if ((event.ctrlKey && event.key === "Delete") || (event.ctrlKey && event.key === "Backspace")) {
				this.$emit("drawing:removed")
				this.removePdfDrawing()
			}
			// duplicate
			if (event.ctrlKey && event.key === "d") {
				event.preventDefault()
				let found = this.drawings?.find(item => item.id === this.selectedDrawingId)
				let createInput = { ...found }
				delete createInput.id
				this.addPdfDrawing(createInput)
			}
			if (event.ctrlKey && event.key === "1") {
				event.preventDefault()
				this.$emit("activeTool:set", "select")
			}
			if (event.ctrlKey && event.key === "2") {
				event.preventDefault()
				this.$emit("activeTool:set", "rectangle")
			}
		}
	},

	watch: {
		height: {
			handler() {
				this.editor.setHeight(this.height)
				this.renderDrawings()
			}
		},
		width: {
			handler() {
				this.editor.setWidth(this.width)
				this.renderDrawings()
			}
		},
		page: {
			handler() {
				this.renderDrawings()
			}
		},
		drawings: {
			handler() {
				this.renderDrawings()
			},
			deep: true
		},
		activeTool: {
			handler() {
				this.renderDrawings()
			}
		}
	}
}
</script>
